import {Howl, Howler} from "howler";
import * as PIXI from "pixi.js";
import {config} from "../config";
import {ApiService} from "./api/ApiService";
import {DiceBet} from "./api/DiceBet";
import {ServerResponse} from "./api/serverResponseClasses/ServerResponse";
import data from "./configs/data";
import {Button} from "./controlElements/Button";
import {InfoMenu} from "./InfoMenu";
import {Menu} from "./Menu";
import {PixiApplication} from "./PixiApplication";
import {PredictionPicker} from "./PredictionPicker";
import {ResultModal} from "./ResultModal";
import {BetPanel} from "./userInterface/BetPanel";

export class Dice extends PIXI.Container {

    public static readonly ELEMENTS_SCALE: any = new PIXI.Point(1.29, 1.29);

    private steemAccountName: string;
    private steemConnectToken: string;
    private keychainJWT: string;
    private apiService = new ApiService();

    private betPanel: BetPanel;
    private predictionPicker: PredictionPicker;
    private resultModal: ResultModal;
    private rollButton: Button;

    private readonly LOGO_POSITION = new PIXI.Point(PixiApplication.WIDTH / 2, 80);
    private readonly LOGO_TEXT_POSITION = new PIXI.Point(609, 80);
    private readonly MENU_POSITION = new PIXI.Point(1045, 580);
    private readonly RESULT_MODAL_POSITION = new PIXI.Point(PixiApplication.WIDTH / 2, PixiApplication.HEIGHT / 2);
    private readonly BET_PANEL_POSITION = new PIXI.Point(PixiApplication.WIDTH / 2, 164);
    private readonly PREDICTION_PICKER_POSITION = new PIXI.Point(PixiApplication.WIDTH / 2, 373);
    private readonly ROLL_BUTTON_POSITION = new PIXI.Point(PixiApplication.WIDTH / 2, 670);
    private readonly DEFAULT_ANCHOR_POINT: any = new PIXI.Point(0.5, 0.5);

    private readonly ROLL_BUTTON_TEXT_STYLE = {
        fontSize: 38,
        fontFamily: "RobotoRegular",
        fontWeight: "bolder",
        fill: "#000000",
    };

    private readonly LOGO_CURRENCY_TEXT_STYLE = {
        fontSize: 66,
        fontFamily: "Arial",
        fontWeight: "bolder",
        fill: "#ffffff",
    };

    constructor() {
        super();
        this.createAndAddBetPanel();
        this.createAndAddPredictionPicker();
        this.createAndAddLogo();
        this.createAndAddMenu();
        this.createAndAddRollButton();
        this.createAndAddResultModal();
        this.createAndAddInfoMenu();
        const backgroundThemeSound = new Howl({
            src: [data.audios.backgroundTheme],
            volume: 0.4,
            loop: true,
        });
        backgroundThemeSound.play();
    }

    public setCredentials(steemAccountName: string, steemConnectToken: string, keychainJWT: string): void {
        this.steemAccountName = steemAccountName;
        this.steemConnectToken = steemConnectToken;
        this.keychainJWT = keychainJWT;
    }

    public async initGame(): Promise<any> {
        const accountInfo = await this.apiService.initGame(
            this.steemAccountName, this.steemConnectToken, this.keychainJWT);
        this.betPanel.setBalanceValue(accountInfo.steemAmount);
        this.rollButton.enabled = true;
    }

    private async playRound(): Promise<void> {
        this.rollButton.enabled = false;
        InfoMenu.getInstance().hide();
        this.resultModal.show();
        this.resultModal.showLoadingAnimation();
        const serverResponse: ServerResponse = await this.apiService.getGameResult(
            new DiceBet(this.predictionPicker.getChosenNomber(), this.predictionPicker.isBetBelowChosenNumber(),
                this.betPanel.getBetAmount()), config.baseCurrency);

        await this.resultModal.showResult(
            serverResponse.diceResult.drawnNumber, serverResponse.diceResult.prize, config.baseCurrency);
        this.resultModal.hide();

        this.betPanel.setBalanceValue(serverResponse.accountInfo.steemAmount);
        if (serverResponse.accountInfo.steemAmount > config.minBet) {
            this.rollButton.enabled = true;
        }
        this.sendRoundPlayedEvent();
    }

    private createAndAddBetPanel(): void {
        this.betPanel = new BetPanel();
        this.betPanel.position = this.BET_PANEL_POSITION;
        this.addChild(this.betPanel);
    }

    private createAndAddPredictionPicker(): void {
        this.predictionPicker = new PredictionPicker();
        this.predictionPicker.position = this.PREDICTION_PICKER_POSITION;
        this.predictionPicker.addOnBetFactorChangedListener((value) => {
            this.betPanel.setPayoutFactor(value);
        });
        this.addChild(this.predictionPicker);
    }

    private createAndAddLogo(): void {
        const logo = PIXI.Sprite.fromImage(data.images.logo);
        logo.position = this.LOGO_POSITION;
        logo.scale = Dice.ELEMENTS_SCALE;
        logo.anchor = this.DEFAULT_ANCHOR_POINT;

        const valueText = new PIXI.Text(config.displayCurrency, this.LOGO_CURRENCY_TEXT_STYLE);
        valueText.position = this.LOGO_TEXT_POSITION;
        valueText.anchor = this.DEFAULT_ANCHOR_POINT;
        (window as any).valueText = valueText;

        this.addChild(logo);
        this.addChild(valueText);
    }

    private createAndAddMenu(): void {
        const menu = new Menu();
        menu.position = this.MENU_POSITION;
        this.addChild(menu);
    }

    private createAndAddResultModal(): void {
        this.resultModal = new ResultModal();
        this.resultModal.position = this.RESULT_MODAL_POSITION;
        this.addChild(this.resultModal);
    }

    private createAndAddInfoMenu(): void {
        InfoMenu.getInstance().position = this.RESULT_MODAL_POSITION;
        this.addChild(InfoMenu.getInstance());
    }

    private createAndAddRollButton(): void {
        this.rollButton = new Button(data.images.rollButtonBase, data.images.rollButtonHover,
            data.images.rollButtonClick, data.images.rollButtonInActive, data.audios.buttonRollClicked);
        this.rollButton.position = this.ROLL_BUTTON_POSITION;
        this.rollButton.label = new PIXI.Text("ROLL", this.ROLL_BUTTON_TEXT_STYLE);
        this.rollButton.label.position.y = -5;
        this.rollButton.label.anchor = this.DEFAULT_ANCHOR_POINT;
        this.rollButton.subscribeOnClicked(() => {
            this.playRound();
        });
        this.rollButton.enabled = false;
        this.addChild(this.rollButton);
    }

    private sendRoundPlayedEvent(): void {
        if (window.parent) {
            window.parent.postMessage("DiceRoundPlayed", "*");
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/Dice.ts